import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api";
import { useClickOutside } from "../../lib/useClickOutside";

// Tipos espelham apps/api/src/carmillion_api/routers/tasks.py.
export interface TaskCard {
  id: string;
  title: string;
  subjects: string[];
  agent_id: string;
  agent_name: string;
  manager_id: string | null;
  manager_name: string | null;
  due_date: string;
  due_time: string | null;
  status: "open" | "done";
  overdue: boolean;
  source: "manual" | "api";
  source_system: string | null;
  postpones: number;
  attachments: number;
  created_at: string;
  finished_at: string | null;
}

export interface DueChange {
  at: string;
  by_name: string;
  from_date: string;
  from_time: string | null;
  to_date: string;
  to_time: string | null;
  reason: string;
}

export interface TaskAttachmentT {
  id: string;
  filename: string;
  content_type: string;
  size: number;
  uploaded_by_name: string | null;
  created_at: string;
  can_delete: boolean;
}

export interface TaskDetail extends Omit<TaskCard, "attachments"> {
  description: string;
  created_by_name: string | null;
  finished_by_name: string | null;
  finish_note: string | null;
  due_changes: DueChange[];
  attachments: TaskAttachmentT[];
  can_act: boolean;
  can_attach: boolean;
  can_delete: boolean;
}

export interface AgentOpt {
  id: string;
  name: string;
  manager_id: string;
  manager_name: string | null;
}

export const MIN_TEXT = 30;

// ── Datas (sempre horário de SP) ───────────────────────────────────────────
export function todaySp() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
}

export function shiftDay(day: string, delta: number) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

export function fmtDay(day: string) {
  return `${day.slice(8, 10)}/${day.slice(5, 7)}`;
}

export function fmtDue(day: string, time: string | null) {
  const today = todaySp();
  const label = day === today ? "Hoje" : day === shiftDay(today, 1) ? "Amanhã" : fmtDay(day);
  return time ? `${label} · ${time}` : label;
}

// "Atrasada há X": sem hora conta dias de calendário; com hora, min/h/dias desde o horário (BRT = UTC-3 fixo).
export function overdueLabel(day: string, time: string | null) {
  const days = (d: number) => `Atrasada há ${d} ${d === 1 ? "dia" : "dias"}`;
  if (!time) return days(Math.round((Date.parse(todaySp()) - Date.parse(day)) / 86_400_000));
  const min = Math.max(1, Math.floor((Date.now() - new Date(`${day}T${time}:00-03:00`).getTime()) / 60_000));
  if (min < 60) return `Atrasada há ${min} min`;
  return min < 1440 ? `Atrasada há ${Math.floor(min / 60)}h` : days(Math.floor(min / 1440));
}

export function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

export function sourceLabel(t: Pick<TaskCard, "source" | "source_system">) {
  if (t.source === "manual") return "Manual";
  return t.source_system ? `API - ${t.source_system}` : "API";
}

export function errMsg(e: unknown, fallback = "Não foi possível concluir.") {
  if (!(e instanceof ApiError)) return fallback;
  const detail = (e.payload as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg).replace(/^Value error, /, "");
  return fallback;
}

// ── Anexos: espelha _TYPES do backend ──────────────────────────────────────
const MB = 1024 * 1024;
const IMAGE = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const VIDEO = ["video/mp4", "video/webm", "video/quicktime"];
const DOCS = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv",
  "text/plain",
];
export const FILE_ACCEPT = [...IMAGE, ...VIDEO, ...DOCS].join(",");

export function fileProblem(f: File): string | null {
  const max = IMAGE.includes(f.type) ? 10 : VIDEO.includes(f.type) ? 100 : DOCS.includes(f.type) ? 25 : 0;
  if (!max) return `${f.name}: tipo não suportado.`;
  if (f.size > max * MB) return `${f.name}: passa do limite de ${max} MB.`;
  return null;
}

export function fmtSize(n: number) {
  return n >= MB ? `${(n / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

export function attachmentUrl(taskId: string, attId: string) {
  return `/api/tasks/${taskId}/attachments/${attId}`;
}

// Upload fora do `api` (que força JSON e timeout de 15s): body raw, nome no header.
export async function uploadAttachment(taskId: string, file: File) {
  const resp = await fetch(`/api/tasks/${taskId}/attachments`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": file.type, "x-filename": encodeURIComponent(file.name) },
    body: file,
  });
  if (!resp.ok) {
    let body: unknown = null;
    try { body = await resp.json(); } catch {}
    throw new ApiError(resp.status, body);
  }
}

// ── UI ─────────────────────────────────────────────────────────────────────
export function Pill({ children, color = "var(--ink-secondary)", bg = "var(--surface-hover)", line = "var(--line)", title }: {
  children: React.ReactNode; color?: string; bg?: string; line?: string; title?: string;
}) {
  return (
    <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, lineHeight: "16px", padding: "1px 8px", borderRadius: 99, color, background: bg, border: `1px solid ${line}`, whiteSpace: "nowrap", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>
      {children}
    </span>
  );
}

export function SourcePill({ t }: { t: Pick<TaskCard, "source" | "source_system"> }) {
  return t.source === "api"
    ? <Pill color="var(--violet)" bg="var(--violet-soft)" line="var(--violet-soft)">{sourceLabel(t)}</Pill>
    : <Pill>{sourceLabel(t)}</Pill>;
}

export function SubjectPills({ subjects, max = 2 }: { subjects: string[]; max?: number }) {
  const shown = subjects.slice(0, max);
  const rest = subjects.slice(max);
  return (
    <>
      {shown.map(s => <Pill key={s} color="var(--info)" bg="var(--info-soft)" line="var(--info-line)">{s}</Pill>)}
      {rest.length > 0 && <Pill title={rest.join(", ")}>+{rest.length}</Pill>}
    </>
  );
}

export function useTaskOptions() {
  return useQuery({ queryKey: ["tasks", "options"], queryFn: () => api.get<{ agents: AgentOpt[] }>("/tasks/options") });
}

// Tags livres com autocomplete: digita → aparecem as existentes; Enter/vírgula cria nova.
export function SubjectInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, open, () => setOpen(false));
  const { data: all = [] } = useQuery({ queryKey: ["tasks", "subjects"], queryFn: () => api.get<string[]>("/tasks/subjects") });
  const lower = new Set(value.map(v => v.toLowerCase()));
  const term = q.trim().toLowerCase();
  const suggestions = all.filter(s => !lower.has(s.toLowerCase()) && (!term || s.toLowerCase().includes(term))).slice(0, 8);
  const exact = all.some(s => s.toLowerCase() === term);

  function add(raw: string) {
    const s = raw.trim().replace(/\s+/g, " ").slice(0, 40);
    if (!s || lower.has(s.toLowerCase()) || value.length >= 10) return;
    onChange([...value, all.find(x => x.toLowerCase() === s.toLowerCase()) ?? s]);
    setQ("");
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", ...inputStyle, padding: "6px 8px" }}>
        {value.map(s => (
          <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "2px 8px", borderRadius: 99, color: "var(--info)", background: "var(--info-soft)", border: "1px solid var(--info-line)" }}>
            {s}
            <button type="button" aria-label={`Remover ${s}`} onClick={() => onChange(value.filter(x => x !== s))} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
          </span>
        ))}
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(q); }
            else if (e.key === "Backspace" && !q && value.length) onChange(value.slice(0, -1));
            else if (e.key === "Escape") setOpen(false);
          }}
          placeholder={value.length ? "" : "Revisão, ajuste de bug…"}
          style={{ flex: 1, minWidth: 120, border: "none", outline: "none", background: "transparent", color: "var(--ink)", fontSize: 13 }}
        />
      </div>
      {open && (suggestions.length > 0 || (term && !exact)) && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 10, background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 8, boxShadow: "var(--shadow-pop)", padding: 4, maxHeight: 220, overflowY: "auto" }}>
          {suggestions.map(s => (
            <div key={s} onMouseDown={e => { e.preventDefault(); add(s); }} style={optionStyle}>{s}</div>
          ))}
          {term && !exact && (
            <div onMouseDown={e => { e.preventDefault(); add(q); }} style={{ ...optionStyle, color: "var(--brand)" }}>
              + Criar “{q.trim()}”
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const optionStyle: React.CSSProperties = { padding: "7px 10px", borderRadius: 6, fontSize: 12.5, cursor: "pointer", color: "var(--ink)" };

export const inputStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", background: "var(--surface-input)", border: "1px solid var(--line)",
  borderRadius: 8, padding: "8px 10px", color: "var(--ink)", fontSize: 13, fontFamily: "inherit",
};

export const labelStyle: React.CSSProperties = { fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6, display: "block" };

export const primaryBtn: React.CSSProperties = { background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" };

export const ghostBtn: React.CSSProperties = { background: "var(--surface-input)", color: "var(--ink-secondary)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" };

export function MinTextCounter({ text }: { text: string }) {
  const n = text.trim().length;
  return <div style={{ fontSize: 11, color: n >= MIN_TEXT ? "var(--ok)" : "var(--ink-muted)" }}>{n}/{MIN_TEXT} caracteres</div>;
}

export function PaperclipIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }} aria-hidden>
      <path d="m21.4 11.1-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" />
    </svg>
  );
}
